import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import Cookies from '@comeon/tomcat-cookies';

import { authenticationSelector, postAuthLocationSelector } from '~/components/authentication/ducks/selectors';
import { isFunction } from '~/components/authentication/utils';
import StepWizard from '~/components/authentication/wizard';
import { selectLicense } from '~/components/brand/ducks/selectors';
import { useCloseCurrentContext } from '~/hooks/use-close-context';
import useRouterCallbacks from '~/hooks/use-router-callbacks';

import config from './config';
import { FLOW_MODE } from './utils/constants';
import WizardWrapper from './wrapper';

export default function PlayNow() {
	const dispatch = useDispatch();
	const { step: stepParam } = useParams();

	const license = useSelector(selectLicense);

	const { FLOW: flow, resetCallbacks, getSteps, TRANSLATIONKEYS, stateSelector, STEPMAP } = config(license);
	const stateData = useSelector(stateSelector);
	const authenticated = useSelector(authenticationSelector);
	const postAuthLocation = useSelector(postAuthLocationSelector);

	const { transformPathAndReplace } = useRouterCallbacks();
	const closeCurrentContext = useCloseCurrentContext();

	const steps = useMemo(() => getSteps(stateData), [getSteps, stateData]);

	const complete = useCallback(
		finalPath => {
			closeCurrentContext();

			const storeActions = resetCallbacks();
			const postPath = finalPath ?? postAuthLocation?.pathname ?? postAuthLocation;

			if (authenticated && postPath) {
				transformPathAndReplace(postPath);
			}

			storeActions.forEach(action => isFunction(action) && dispatch(action()));
		},
		[closeCurrentContext, resetCallbacks, postAuthLocation, authenticated, transformPathAndReplace, dispatch]
	);

	const navigate = useCallback(
		stepDetails => {
			if (stepDetails?.url) {
				transformPathAndReplace(stepDetails.url);
			} else {
				transformPathAndReplace(`/step/${stepDetails.key}`);
			}
		},
		[transformPathAndReplace]
	);

	const init = useCallback(() => {
		const currentStep = steps[stepParam];

		if (currentStep && currentStep.isValid()) {
			return stepParam;
		}

		const validStep = flow.filter(f => steps[f]?.isValid())[0];

		return validStep;
	}, [flow, stepParam, steps]);

	const exit = complete;

	/**
	 * To fix the url where there is descrepancy in the url and widget shown
	 */
	useEffect(() => {
		const step = init();

		if (stepParam && step !== stepParam && !steps[stepParam]?.isValid() && !authenticated) {
			navigate(steps[step]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		Cookies.set('registration-method', FLOW_MODE);
	}, []);

	const wizardProps = useMemo(
		() => ({
			complete,
			exit,
			flow,
			init,
			next: navigate,
			previous: navigate,
			steps,
			translationKeys: TRANSLATIONKEYS
		}),
		[complete, exit, flow, init, navigate, steps, TRANSLATIONKEYS]
	);

	return (
		<StepWizard {...wizardProps}>
			<WizardWrapper STEPMAP={STEPMAP} />
		</StepWizard>
	);
}
